import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useTranslation } from '../../i18n/LocaleContext';

// 社交账号绑定审核：待审核列表 + 通过/驳回（驳回原因展示给用户）
const PLATFORM_LABELS = { tiktok: 'TikTok', facebook: 'Facebook', instagram: 'Instagram', youtube: 'YouTube' };

const AdminSocials = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('pending');
  const [socials, setSocials] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });

  useEffect(() => {
    fetchSocials();
  }, [filter, page]);

  const fetchSocials = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSocials(filter, 50, page);
      setSocials(data.socials || []);
      setPendingCount(Number(data.pending_count || 0));
      setPagination(data.pagination || { total: 0, total_pages: 1 });
    } catch (error) {
      console.error('Fetch socials error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm(t('aSocialApproveConfirm'))) return;
    try {
      setProcessing(id);
      await adminService.approveSocial(id);
      alert(t('aSocialApprovedMsg'));
      fetchSocials();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(t('aSocialRejectAsk'), '');
    if (reason === null) return;
    try {
      setProcessing(id);
      await adminService.rejectSocial(id, reason.trim());
      alert(t('aSocialRejectedMsg'));
      fetchSocials();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-900 bg-opacity-30 border border-yellow-700', text: 'text-yellow-400', label: t('aSocialPending'), icon: Clock },
      approved: { bg: 'bg-green-900 bg-opacity-30 border border-green-700', text: 'text-green-400', label: t('aSocialApproved'), icon: CheckCircle },
      rejected: { bg: 'bg-red-900 bg-opacity-30 border border-red-700', text: 'text-red-400', label: t('aSocialRejected'), icon: XCircle }
    };
    const badge = badges[status];
    if (!badge) return null;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const filters = [
    { key: 'all', label: t('aSocialAll') },
    { key: 'pending', label: t('aSocialPending') },
    { key: 'approved', label: t('aSocialApproved') },
    { key: 'rejected', label: t('aSocialRejected') }
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aSocialTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aSocialSubtitle')}</p>
      </div>

      {pendingCount > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-400" />
            <p className="text-sm text-gray-400">
              <strong>{t('aSocialPendingCount', { n: pendingCount })}</strong> {t('aSocialPendingSuffix')}
            </p>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === f.key ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aSocialUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycPhone')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aSocialPlatform')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aSocialAccount')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aSocialStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">{t('aKycLoading')}</td>
                </tr>
              ) : socials.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">{t('aKycEmpty')}</td>
                </tr>
              ) : socials.map((social) => (
                <tr key={social.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{social.id}</td>
                  <td className="px-6 py-4 text-sm text-white">{social.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-300 font-mono">{social.phone}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{PLATFORM_LABELS[social.platform] || social.platform}</td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-white">{social.handle}</p>
                    <a
                      href={social.account_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-brand-400 hover:text-brand-300 underline truncate max-w-56 inline-block"
                    >
                      {social.account_url}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(social.status)}
                    {social.status === 'rejected' && social.reject_reason && (
                      <p className="mt-1 max-w-40 text-xs text-gray-500">{social.reject_reason}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(social.created_at).toLocaleString('vi-VN')}</td>
                  <td className="px-6 py-4">
                    {social.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(social.id)}
                          disabled={processing === social.id}
                          className="px-3 py-1 bg-brand-500 text-white rounded-lg text-xs hover:bg-brand-600 disabled:opacity-50"
                        >
                          {t('aKycApprove')}
                        </button>
                        <button
                          onClick={() => handleReject(social.id)}
                          disabled={processing === social.id}
                          className="px-3 py-1 bg-gray-700 text-white rounded-lg text-xs hover:bg-gray-600 disabled:opacity-50"
                        >
                          {t('aSocialRejected')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {pagination.total > 0 && <div className="mt-4 flex items-center justify-between text-sm text-gray-400"><span>{t('aKycPaginationTotal', { total: pagination.total, page, pages: pagination.total_pages })}</span><div className="flex gap-2"><button onClick={() => setPage(page - 1)} disabled={page === 1} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aKycPrev')}</button><button onClick={() => setPage(page + 1)} disabled={page >= pagination.total_pages} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aKycNext')}</button></div></div>}
    </div>
  );
};

export default AdminSocials;
